import { useEffect, useMemo, useState } from "react";
import { Plus, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, Panel, StatCard, StatusBadge, NativeSelect } from "@/components/crm/common";
import { DataTable } from "@/components/crm/DataTable";
import { FormDialog } from "@/components/crm/FormDialog";

const STATUS = ["Active", "On leave", "Notice period", "Inactive"];

function OrgLists({ org, onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button data-testid="org-lists-button" variant="outline" onClick={() => setOpen(true)}><Settings2 className="mr-1 h-4 w-4" />Departments & designations</Button>
      <FormDialog testid="org-lists-form" open={open} onOpenChange={setOpen} title="Departments & designations" submitLabel="Save lists"
        initial={{ departments: org.departments.join("\n"), designations: org.designations.join("\n") }}
        fields={[{ name: "departments", label: "Departments (one per line)", type: "textarea", full: true, required: true }, { name: "designations", label: "Designations (one per line)", type: "textarea", full: true }]}
        onSubmit={async (f) => {
          try { await api.put("/org", { departments: f.departments.split("\n"), designations: (f.designations || "").split("\n") }); toast.success("Lists saved"); onSaved(); return true; }
          catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
        }} />
    </>
  );
}

export default function Employees() {
  const { isAdmin, can } = useAuth();
  const team = useCollection("team");
  const { items: tasks } = useCollection("tasks");
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState(null);
  const [dept, setDept] = useState("");
  const [q, setQ] = useState("");
  const loadOrg = () => api.get("/org").then((r) => setOrg(r.data)).catch(() => {});
  useEffect(() => { loadOrg(); }, []);
  const open = useMemo(() => tasks.reduce((m, t) => { if (t.status !== "Complete") m[t.assignee] = (m[t.assignee] || 0) + 1; return m; }, {}), [tasks]);
  if (!org) return <PageHeader num="24" title="Employee Directory" subtitle="Loading…" />;
  const depts = [...org.departments, ...org.legacy_departments];
  const rows = team.items.filter((t) => (!dept || t.department === dept) && (!q || `${t.name} ${t.title} ${t.employee_code}`.toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => (a.employee_code || "").localeCompare(b.employee_code || ""));
  const editable = isAdmin || can("Team", "edit");
  return (
    <>
      <PageHeader num="24" title="Employee Directory" subtitle="Every team member with employee ID, department, designation, reporting manager, status and open work.">
        {isAdmin && <OrgLists org={org} onSaved={loadOrg} />}
        {editable && <Button data-testid="employee-add-button" onClick={() => setForm({ status: "Active" })}><Plus className="mr-1 h-4 w-4" />Add employee</Button>}
      </PageHeader>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard testid="emp-stat-total" label="Employees" value={team.items.length} />
        <StatCard testid="emp-stat-active" label="Active" value={team.items.filter((t) => (t.status || "Active") === "Active").length} />
        <StatCard testid="emp-stat-depts" label="Departments" value={new Set(team.items.map((t) => t.department).filter(Boolean)).size} />
        <StatCard testid="emp-stat-logins" label="With CRM login" value={team.items.filter((t) => t.user_id).length} />
      </div>
      <Panel>
        <div className="mb-3 flex flex-wrap gap-2">
          <input data-testid="employee-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, designation or ID" className="h-9 w-64 rounded-lg border px-3 text-sm outline-none" />
          <NativeSelect testid="employee-dept-filter" value={dept} onChange={setDept} placeholder="All departments" options={depts} />
        </div>
        <DataTable testid="employee-table" rows={rows} onRowClick={editable ? (r) => setForm(r) : undefined} columns={[
          { key: "employee_code", label: "ID", render: (r) => <span className="font-mono text-xs font-bold text-[#C5A46D]">{r.employee_code || "—"}</span> },
          { key: "name", label: "Employee", render: (r) => <div className="flex items-center gap-2"><span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#C5A46D] text-xs font-extrabold text-[#171923]">{r.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span><div><div className="font-bold">{r.name}</div><div className="text-xs text-zinc-500">{r.email || r.phone || ""}</div></div></div> },
          { key: "title", label: "Designation" }, { key: "department", label: "Department" }, { key: "reports_to", label: "Reports to" },
          { key: "joined", label: "Joined" }, { key: "open", label: "Open tasks", render: (r) => open[r.name] || 0 },
          { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status || "Active"} /> },
        ]} />
      </Panel>
      <FormDialog testid="employee-form" open={!!form} onOpenChange={(o) => !o && setForm(null)} title={form?.id ? `Edit ${form.name}` : "Add employee"} initial={form}
        fields={[{ name: "name", label: "Full name", required: true }, { name: "title", label: "Designation", type: "select", options: org.designations, required: true },
          { name: "department", label: "Department", type: "select", options: depts, required: true }, { name: "reports_to", label: "Reporting manager", type: "select", options: team.items.map((t) => t.name) },
          { name: "email", label: "Email" }, { name: "phone", label: "Phone" }, { name: "joined", label: "Joining date", type: "date" },
          { name: "status", label: "Employment status", type: "select", options: STATUS, required: true }]}
        onSubmit={async (f) => !!(f.id ? await team.update(f.id, f, "Employee updated") : await team.create(f, "Employee added"))} />
    </>
  );
}
